import { useI18n } from "../i18n/useI18n";
import { numberLocaleFor } from "../i18n/locale";
import type { UiKey } from "../i18n/ui";
import type { AudienceMode } from "../lib/audienceMode";
import { estimateUncertaintyBand } from "../lib/audienceMode";
import type { DisplayCurrency } from "../lib/displayCurrency";
import { usdToEurIndicative } from "../lib/displayCurrency";
import type { CostDisplayUnit } from "../lib/persistedUi";
import type { CostEstimate } from "../lib/types";
import type { Workload } from "../lib/types";

const UNITS: CostDisplayUnit[] = ["month", "year", "perDau", "perMillionReq"];

/** Рядки з діапазоном low–high (ті самі множники, що й для total). */
const LINE_BAND_IDS = new Set(["lambda", "llm", "dynamo"]);

type Props = {
  estimate: CostEstimate;
  effectiveWorkload: Workload;
  displayUnit: CostDisplayUnit;
  onDisplayUnit: (u: CostDisplayUnit) => void;
  audienceMode: AudienceMode;
  displayCurrency: DisplayCurrency;
};

function toDisplayValue(
  monthlyUsd: number,
  unit: CostDisplayUnit,
  w: Workload,
  monthlyRequests: number,
): number {
  switch (unit) {
    case "year":
      return monthlyUsd * 12;
    case "perDau":
      return monthlyUsd / Math.max(1, w.usersDaily);
    case "perMillionReq":
      return (monthlyUsd / Math.max(1, monthlyRequests)) * 1e6;
    default:
      return monthlyUsd;
  }
}

export function CostBreakdown({
  estimate,
  effectiveWorkload,
  displayUnit,
  onDisplayUnit,
  audienceMode,
  displayCurrency,
}: Props) {
  const { locale, t, tf } = useI18n();
  const nf = numberLocaleFor(locale);

  const monthlyRequests =
    effectiveWorkload.usersDaily * effectiveWorkload.requestsPerUser * 30;

  const cur = displayCurrency === "EUR" ? "EUR" : "USD";
  const fromUsdMonthly = (usdMo: number) =>
    displayCurrency === "EUR" ? usdToEurIndicative(usdMo) : usdMo;

  const fmt = (n: number) =>
    n.toLocaleString(nf, {
      style: "currency",
      currency: cur,
      maximumFractionDigits: 0,
    });

  const m = estimate.totalMonthlyUsd;
  const mDisp = fromUsdMonthly(m);
  const { value, suffixKey } = (() => {
    switch (displayUnit) {
      case "year":
        return { value: mDisp * 12, suffixKey: "breakdown.unitSuffix.year" as const };
      case "perDau": {
        const d = Math.max(1, effectiveWorkload.usersDaily);
        return { value: mDisp / d, suffixKey: "breakdown.unitSuffix.perDau" as const };
      }
      case "perMillionReq": {
        const r = Math.max(1, monthlyRequests);
        return {
          value: (mDisp / r) * 1e6,
          suffixKey: "breakdown.unitSuffix.perMillionReq" as const,
        };
      }
      default:
        return { value: mDisp, suffixKey: "breakdown.unitSuffix.month" as const };
    }
  })();

  const band = estimateUncertaintyBand(audienceMode);
  const lowM = m * band.low;
  const highM = m * band.high;
  const lowV = toDisplayValue(
    fromUsdMonthly(lowM),
    displayUnit,
    effectiveWorkload,
    monthlyRequests,
  );
  const highV = toDisplayValue(
    fromUsdMonthly(highM),
    displayUnit,
    effectiveWorkload,
    monthlyRequests,
  );
  const rangeText = tf(
    audienceMode === "simple"
      ? "breakdown.rangeBandSimple"
      : "breakdown.rangeBandTechnical",
    { low: fmt(Math.min(lowV, highV)), high: fmt(Math.max(lowV, highV)) },
  );

  const UNIT_LABEL: Record<CostDisplayUnit, UiKey> = {
    month: "breakdown.unit.month",
    year: "breakdown.unit.year",
    perDau: "breakdown.unit.perDau",
    perMillionReq: "breakdown.unit.perMillionReq",
  };

  return (
    <div>
      <h2>{t("breakdown.title")}</h2>
      <details className="glossary">
        <summary>{t("glossary.summary")}</summary>
        <dl>
          <dt>{t("glossary.termDau")}</dt>
          <dd>{t("glossary.defDau")}</dd>
          <dt>{t("glossary.termGb")}</dt>
          <dd>{t("glossary.defGb")}</dd>
          <dt>{t("glossary.termEgress")}</dt>
          <dd>{t("glossary.defEgress")}</dd>
        </dl>
      </details>
      <ul className="lines">
        {estimate.lineItems.map((row) => (
          <li key={row.id}>
            <div className="row-top">
              <span className="label">{row.label}</span>
              <span className="amt">{fmt(fromUsdMonthly(row.monthlyUsd))}</span>
            </div>
            <span className="detail">{row.detail}</span>
            {LINE_BAND_IDS.has(row.id) ? (
              <span className="line-band">
                {tf("breakdown.lineRange", {
                  low: fmt(fromUsdMonthly(row.monthlyUsd * band.low)),
                  high: fmt(fromUsdMonthly(row.monthlyUsd * band.high)),
                })}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="unit-hint">{t("breakdown.linesMonthlyHint")}</p>
      <div className="unit-row">
        <span className="unit-label">{t("breakdown.showTotalAs")}</span>
        <div
          className="unit-tabs"
          role="tablist"
          aria-label={t("breakdown.showTotalAs")}
        >
          {UNITS.map((u) => (
            <button
              key={u}
              type="button"
              role="tab"
              aria-selected={displayUnit === u}
              className={displayUnit === u ? "u-tab active" : "u-tab"}
              onClick={() => onDisplayUnit(u)}
            >
              {t(UNIT_LABEL[u])}
            </button>
          ))}
        </div>
      </div>
      <div className="total">
        <span>{t("breakdown.total")}</span>
        <strong>{fmt(value)}</strong>
        <span className="per">{t(suffixKey)}</span>
      </div>
      <p className="range-band">{rangeText}</p>
      <ul className="assumptions">
        {estimate.assumptions.map((a, i) => (
          <li key={i}>{a}</li>
        ))}
      </ul>

      <style>{`
        h2 {
          font-size: 1rem;
          margin: 0 0 1rem;
        }
        .glossary {
          margin: 0 0 1rem;
          padding: 0.55rem 0.75rem;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: var(--surface2);
          font-size: 0.8rem;
        }
        .glossary summary {
          cursor: pointer;
          font-weight: 600;
          color: var(--muted);
          list-style-position: outside;
        }
        .glossary summary:hover {
          color: var(--text);
        }
        .glossary dl {
          margin: 0.65rem 0 0.15rem;
          padding: 0;
        }
        .glossary dt {
          margin-top: 0.5rem;
          font-weight: 600;
          color: var(--text);
        }
        .glossary dt:first-child {
          margin-top: 0;
        }
        .glossary dd {
          margin: 0.2rem 0 0;
          padding: 0;
          color: var(--muted);
          line-height: 1.45;
        }
        .lines {
          list-style: none;
          margin: 0;
          padding: 0;
        }
        .lines li {
          padding: 0.65rem 0;
          border-bottom: 1px solid var(--border);
        }
        .lines li:last-child {
          border-bottom: none;
        }
        .row-top {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 1rem;
        }
        .label {
          font-weight: 500;
        }
        .amt {
          font-family: var(--mono);
          font-size: 0.9rem;
          color: var(--accent);
        }
        .detail {
          display: block;
          font-size: 0.8rem;
          color: var(--muted);
          margin-top: 0.2rem;
        }
        .line-band {
          display: block;
          font-size: 0.74rem;
          color: var(--muted);
          margin-top: 0.35rem;
          line-height: 1.4;
        }
        .unit-hint {
          margin: 0.75rem 0 0.5rem;
          font-size: 0.78rem;
          color: var(--muted);
          line-height: 1.4;
        }
        .unit-row {
          margin-bottom: 0.65rem;
        }
        .unit-label {
          display: block;
          font-size: 0.78rem;
          color: var(--muted);
          margin-bottom: 0.4rem;
        }
        .unit-tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 0.4rem;
        }
        .u-tab {
          border: 1px solid var(--border);
          background: var(--surface2);
          color: var(--muted);
          padding: 0.3rem 0.55rem;
          border-radius: 8px;
          cursor: pointer;
          font: inherit;
          font-size: 0.78rem;
          font-weight: 600;
        }
        .u-tab.active {
          border-color: var(--accent-dim);
          color: var(--text);
          background: var(--accent-chip);
        }
        .u-tab:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 2px;
        }
        @media (max-width: 640px) {
          .u-tab {
            min-height: 40px;
            padding: 0.45rem 0.65rem;
            font-size: 0.8rem;
          }
        }
        .total {
          margin-top: 0.25rem;
          padding: 0.85rem 1rem;
          background: var(--surface2);
          border-radius: 8px;
          display: flex;
          align-items: baseline;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .total strong {
          font-family: var(--mono);
          font-size: 1.25rem;
          color: var(--accent);
        }
        .per {
          color: var(--muted);
          font-size: 0.85rem;
        }
        .range-band {
          margin: 0.5rem 0 0;
          font-size: 0.82rem;
          line-height: 1.45;
          color: var(--muted);
          min-height: 4.5rem;
          min-height: 5lh;
        }
        .assumptions {
          margin: 1rem 0 0;
          padding-left: 1.1rem;
          font-size: 0.8rem;
          color: var(--muted);
        }
      `}</style>
    </div>
  );
}
